import { FC, useContext, useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import { Controller } from "react-hook-form";

import HubInputField from "../../inputType";

import { PATIENT_INSURANCE_TYPE } from "src/constants/cases";
import { InsuranceResponseType, PlanResponseType, SingleInsuranceProps } from "./types";
import ReactInputMask from "react-input-mask";
import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { useDebouncedCallback } from "use-debounce";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import { ToastTypes } from "src/types";
import InsuranceTitle from "src/components/header/insuranceTitle";
import PlanNameTitle from "src/components/header/planNameTitle";


type ListingOption = {
  value: string;
  label: string;
};

const SinglePatientInsurance: FC<SingleInsuranceProps> = ({ index, control, watch, removeInsurance, setValue }): JSX.Element => {
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [pendingOtherIndex, setPendingOtherIndex] = useState<number | null>(null);

  const relationType = watch(`insurances_attributes.${index}.relation_to_patient`);
  const insuranceName = watch(`insurances_attributes.${index}.insurance_name`);
  const [insuranceInput, setInsuranceInput] = useState("");
  const [_, setInsuranceInputId] = useState<number | null>(null);

  const insuranceOptionSelectedRef = useRef(false);
  const [planInput, setPlanInput] = useState("");
  const planOptionSelectedRef = useRef(false);
  const { showToast } = useContext(ToastContext);

  /**
   * @SearchDiagnosisBySearchValue search by API for diagnosis
   * @first initialize useFetch() with diagnosis
   * @second search diagnosis by search term
   */
  const { data: planNameListing, mutate: getplanName, error: planNameError, removeErrorAndData: clearPlanNameData } =
    useFetch<PlanResponseType>(APIEndpoints.PLAN_DROPDOWN);

  /** @second */
  const searchPlanName = (search: string) => {

    clearPlanNameData();
    getplanName(null, null, {
      master_insurance_id: search,
    });
  }




  const debouncedPlanName = useDebouncedCallback(searchPlanName, 100);

  useEffect(() => {
    if (planNameError) {
      console.error("Error fetching plan name data:", planNameError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planNameListing, planNameError]);


  /**
      * @SearchDiagnosisBySearchValue search by API for diagnosis
      * @first initialize useFetch() with diagnosis
      * @second search diagnosis by search term
      */
  const { data: insuranceListing, mutate: getInsurance, error: insuranceError, removeErrorAndData: clearInsuranceData } =
    useFetch<InsuranceResponseType>(APIEndpoints.INSURANCE_DROPDOWN);

  /** @second */
  const searchInsurance = (search: string) => {
    const searchText = search.trim();

    if (searchText.length >= 2) {
      clearInsuranceData();
      console.log("searchInsurance called with search text:", searchText);
      getInsurance(null, null, { text: searchText });
    }
  };

  const debouncedInsurance = useDebouncedCallback(searchInsurance, 100);

  useEffect(() => {
    if (insuranceListing && insuranceListing.data) {
      if (insuranceListing.data.length === 0) {
        showToast([`No insurance found for "${insuranceInput.trim()}". Press Enter to use "${insuranceInput.trim()}".`], ToastTypes.WARNING);
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceListing, insuranceError]);

  return (
    <div className="card p-3 mb-3 border-light-subtle">
      <div className="row">

        <div className="col-lg-6 col-md-6">

          <div className="row pb-3">

            <Controller
              name={`insurances_attributes.${index}.insurance_name`}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => {
                const insuranceOptions: ListingOption[] =
                  insuranceListing?.data?.map((item) => ({
                    value: String(item.id),
                    label: item.insurance_name,
                  })) ?? [];

                const selectedInsurance: ListingOption | null = field.value
                  ? insuranceOptions.find(
                    (option) =>
                      option.label === String(field.value) ||
                      option.value === String(field.value)
                  ) ?? {
                    value: String(field.value),
                    label: String(field.value),
                  }
                  : null;

                const saveTypedInsurance = () => {
                  const typedValue = insuranceInput.trim();

                  if (!typedValue || insuranceOptionSelectedRef.current) {
                    return;
                  }

                  // Check if typed value matches an existing insurance
                  const matchedInsurance = insuranceOptions.find(
                    (option) =>
                      option.label.toLowerCase() === typedValue.toLowerCase()
                  );

                  if (matchedInsurance) {
                    // Save insurance name
                    field.onChange(matchedInsurance.label);

                    // Save insurance ID
                    setValue(
                      `insurances_attributes.${index}.master_insurance_id`,
                      Number(matchedInsurance.value),
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    );

                    setInsuranceInputId(Number(matchedInsurance.value));

                    // Load plans using insurance ID
                    debouncedPlanName(matchedInsurance.value);

                    clearPlanNameData();
                  } else {
                    // Custom insurance name
                    field.onChange(typedValue);

                    // Custom insurance doesn't have an existing ID
                    setValue(
                      `insurances_attributes.${index}.master_insurance_id`,
                      0,
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    );

                    setInsuranceInputId(0);

                    // Clear plans because no insurance ID exists
                    clearPlanNameData();
                  }

                  setInsuranceInput("");
                  clearInsuranceData();

                  insuranceOptionSelectedRef.current = true;
                };

                return (
                  <>
                    <label
                      className="form-label col-lg-4 col-md-4 col-12"
                      htmlFor={`insurances_attributes.${index}.insurance_name`}
                    >
                      Insurance Name *
                    </label>

                    <div className="col-lg-8 col-md-8 col-12">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <Select<ListingOption, false>
                            name={field.name}
                            inputId={`insurances_attributes.${index}.insurance_name`}
                            ref={field.ref}
                            options={insuranceOptions}
                            value={selectedInsurance}
                            inputValue={insuranceInput}
                            placeholder="Please Enter Insurance Name"
                            isSearchable
                            isClearable
                            styles={{
                              container: (base) => ({
                                ...base,
                                width: "100%",
                              }),
                            }}

                            // We are loading/searching options from API
                            filterOption={() => true}

                            onChange={(selected) => {
                              insuranceOptionSelectedRef.current = true;

                              setInsuranceInput("");

                              // User cleared the select
                              if (!selected) {
                                field.onChange("");

                                setValue(
                                  `insurances_attributes.${index}.master_insurance_id`,
                                  0,
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  }
                                );

                                // setInsuranceInputId(0);

                                clearPlanNameData();
                                setPlanInput("");

                                return;
                              }


                              // selected.value = insurance ID
                              // selected.label = insurance name

                              field.onChange(selected.label);

                              // Save insurance ID
                              setValue(
                                `insurances_attributes.${index}.master_insurance_id`,
                                Number(selected.value),
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                }
                              );

                              // setInsuranceInputId(Number(selected.value));

                              debouncedInsurance(selected.label);

                              // Load plan names using selected insurance ID
                              debouncedPlanName(selected.value);

                              clearPlanNameData();

                              setPlanInput("");
                              if (selected.label.trim().toLowerCase() === "other") {
                                setPendingOtherIndex(index);
                                setShowOtherModal(true);
                                return;
                              }else {
                                setValue(`insurances_attributes.${index}.is_other_insurance`, "no");
                                return;
                              }
                            }}


                            onInputChange={(inputValue, actionMeta) => {
                              if (actionMeta.action === "input-change") {
                                insuranceOptionSelectedRef.current = false;

                                setInsuranceInput(inputValue);
                                // setInsuranceInputId(0);

                                const searchText = inputValue.trim();

                                if (searchText.length >= 2) {
                                  // Search insurance API
                                  debouncedInsurance(searchText);
                                } else {
                                  debouncedInsurance.cancel();
                                  clearInsuranceData();
                                }
                              }

                              return inputValue;
                            }}

                            onKeyDown={(event) => {
                              if (event.key === "Enter" && insuranceInput.trim()) {
                                const typedValue = insuranceInput.trim();

                                // Check whether typed text matches an existing option
                                const matchedInsurance = insuranceOptions.find(
                                  (option) =>
                                    option.label.toLowerCase() ===
                                    typedValue.toLowerCase()
                                );

                                if (matchedInsurance) {
                                  event.preventDefault();

                                  insuranceOptionSelectedRef.current = true;

                                  // Save insurance name
                                  field.onChange(matchedInsurance.label);

                                  // Save insurance ID
                                  setValue(
                                    `insurances_attributes.${index}.master_insurance_id`,
                                    Number(matchedInsurance.value),
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    }
                                  );

                                  setInsuranceInput("");
                                  // setInsuranceInputId(
                                  //   Number(matchedInsurance.value)
                                  // );

                                  // Load plans
                                  debouncedPlanName(matchedInsurance.value);

                                  clearPlanNameData();

                                  return;
                                }

                                // No insurance found:
                                // save manually typed insurance
                                if (insuranceOptions.length === 0) {
                                  event.preventDefault();

                                  debouncedInsurance.cancel();

                                  saveTypedInsurance();
                                }
                              }
                            }}

                            onBlur={() => {
                              debouncedInsurance.cancel();
                              setInsuranceInput("")
                              // setInsuranceInputId(
                              //      null
                              //     );
                              insuranceOptionSelectedRef.current = false;
                              setPlanInput("");
                              setValue(`insurances_attributes.${index}.plan_name`, "")
                              // Save manually typed value if nothing was selected
                              //saveTypedInsurance();


                              const selectedInsurance = String(field.value ?? "")
                                .trim()
                                .toLowerCase();

                              field.onBlur();

                              // Show modal when focus leaves and Insurance is "Other"
                              // if (selectedInsurance === "other") {
                              //   setPendingOtherIndex(index);
                              //   setShowOtherModal(true);
                              //   return;
                              // }
                            }}

                            noOptionsMessage={() => {
                              if (insuranceInput.trim().length < 2) {
                                return "Please enter 2 or more characters to search...";
                              }

                              return `No insurance found. Press Enter to use "${insuranceInput.trim()}".`;
                            }}
                          />
                        </div>
                        <InsuranceTitle />
                      </div>
                      {error?.message ? (
                        <div className="text-danger text-small mt-2">
                          {error.message}
                        </div>
                      ) : null}

                    </div>


                  </>
                );
              }}
            />
          </div>
        </div>
        {
          <div className="col-lg-6 col-md-6">
            <div className="row pb-3">
              <Controller
                name={`insurances_attributes.${index}.plan_name`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => {
                  const planNameOptions: ListingOption[] =
                    planNameListing?.data?.map((item) => ({
                      value: String(item.id),
                      label: item.name,
                    })) ?? [];


                  const selectedPlanName: ListingOption | null = field.value
                    ? planNameOptions.find(
                      (option) =>
                        option.label === String(field.value) ||
                        option.value === String(field.value)
                    ) ?? {
                      value: String(field.value),
                      label: String(field.value),
                    }
                    : null;

                  const saveTypedPlanName = () => {
                    const typedValue = planInput.trim();


                    if (!typedValue || planOptionSelectedRef.current) {
                      return;
                    }


                    field.onChange(typedValue);

                    setPlanInput("");
                    clearPlanNameData();

                    planOptionSelectedRef.current = true;
                  };

                  return (
                    <>
                      <label
                        className="form-label col-lg-4 col-md-4 col-12"
                        htmlFor={`insurances_attributes.${index}.plan_name`}
                      >
                        Plan Name
                      </label>

                      <div className="col-lg-8 col-md-8 col-12">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <Select<ListingOption, false>
                              name={field.name}
                              inputId={`insurances_attributes.${index}.plan_name`}
                              ref={field.ref}
                              options={planNameOptions}
                              value={selectedPlanName}
                              inputValue={planInput}
                              placeholder="Please Enter Plan Name"
                              isSearchable
                              isClearable
                              filterOption={() => true}
                              styles={{
                                container: (base) => ({
                                  ...base,
                                  width: "100%",
                                }),
                              }}

                              onChange={(selected) => {

                                planOptionSelectedRef.current = true;

                                setPlanInput("");

                                debouncedPlanName.cancel();
                                //clearPlanNameData();


                                if (!selected) {
                                  field.onChange("");
                                  return;
                                }


                                field.onChange(selected.label);
                              }}


                              onInputChange={(inputValue, actionMeta) => {
                                if (actionMeta.action === "input-change") {

                                  planOptionSelectedRef.current = false;

                                  setPlanInput(inputValue);

                                  //const searchText = inputValue.trim();

                                  // if (searchText.length >= 2) {
                                  //   debouncedPlanName(searchText);
                                  // } else {
                                  //   debouncedPlanName.cancel();
                                  //   clearPlanNameData();
                                  // }
                                }

                                return inputValue;
                              }}


                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" &&
                                  planInput.trim() &&
                                  planNameOptions.length === 0
                                ) {
                                  event.preventDefault();

                                  debouncedPlanName.cancel();

                                  saveTypedPlanName();
                                }
                              }}


                              onBlur={() => {
                                debouncedPlanName.cancel();

                                saveTypedPlanName();

                                field.onBlur();
                              }}

                              noOptionsMessage={() => {
                                if (planInput.trim().length < 2) {
                                  return "Please enter 2 or more characters to search...";
                                }

                                return `No plan found. Press Enter to use "${planInput.trim()}".`;
                              }}
                            />
                          </div>
                          <PlanNameTitle />
                        </div>
                        {error?.message ? (
                          <div className="text-danger text-small mt-2">
                            {error.message}
                          </div>
                        ) : null}
                      </div>
                    </>
                  );
                }}
              />
            </div>
          </div>
        } 
        <div className="col-lg-6 col-md-6">
          <Controller
            name={`insurances_attributes.${index}.plan_type`}
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <div className={"row"}>
                <label className="form-label col-lg-4 col-12">Type</label>
                <div className={"col-lg-8 col-12"}>
                  <HubInputField
                    isRowBased={true}
                    isRowInsideAnother={true}
                    isRequired={false}
                    field={field}
                    // inputLabel="Type"
                    inputId={`insurances_attributes.${index}.plan_type`}
                    error={error}
                  />
                </div>
              </div>
            )}
          />
        </div>
        <div className="col-lg-6 col-md-6">
          <Controller
            name={`insurances_attributes.${index}.relation_to_patient`}
            control={control}
            defaultValue="self"
            render={({ field, fieldState: { error } }) => (
              <div className={"row"}>
                <label className="form-label col-lg-4 col-12">Relation to Patient</label>
                <div className={"col-lg-8 col-12"}>
                  <HubInputField
                    field={field}
                    error={error}
                    inputId={`insurances_attributes.${index}.relation_to_patient`}
                    isRequired={false}
                    isRowBased={true}
                    isDropdown={true}
                    isTypeAhead
                    isSearchable={false}
                    dropdownData={PATIENT_INSURANCE_TYPE as unknown as OptionsOrGroups<string, GroupBase<string>>}
                  />
                </div>
              </div>
            )}
          />
        </div>
      </div>

      {relationType !== "self" ? (
        <div className="row">
          <label className="form-label col-lg-2 col-12">Relative Name</label>
          <div className="col-lg-10 col-12">
            <div className="row">
              <div className="col-md-4">
                <Controller
                  name={`insurances_attributes.${index}.relation_firstname`}
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId={`insurances_attributes.${index}.relation_firstname`}
                      inputPlaceholder="First Name"
                      isRequired={true}
                      isRowBased={true}
                      field={field}
                      error={error}
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <Controller
                  name={`insurances_attributes.${index}.relation_middlename`}
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId={`insurances_attributes.${index}.relation_middlename`}
                      inputPlaceholder="Middle Name"
                      isRequired={true}
                      isRowBased={true}
                      field={field}
                      error={error}
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <Controller
                  name={`insurances_attributes.${index}.relation_lastname`}
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId={`insurances_attributes.${index}.relation_lastname`}
                      inputPlaceholder="Last Name"
                      isRequired={true}
                      isRowBased={true}
                      field={field}
                      error={error}
                    />
                  )}
                />
              </div>
            </div>

          </div>
        </div>
      ) : null}

      {/* Medical Insurance */}
      <div className="card mb-3">
        <div className="card-header bg-success-subtle">
          <h5 style={{ fontSize: '1.375rem', color: '#000' }}>Medical Insurance</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-lg-4 col-md-4">
              <Controller
                name={`insurances_attributes.${index}.policy`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">Member ID *</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRowInsideAnother={true}
                        isRequired={true}
                        field={field}
                        // inputLabel="Member ID"
                        inputId={`insurances_attributes.${index}.policy`}
                        inputPlaceholder=""
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="col-lg-4 col-md-4">
              <Controller
                name={`insurances_attributes.${index}.group_number`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">Group{insuranceName?.toLowerCase() === "other" && (
    <span className="text-black">  *</span>
  )}</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRequired={false}
                        field={field}
                        // inputLabel="Group"
                        inputId={`insurances_attributes.${index}.group_number`}
                        inputPlaceholder=""
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
            <Controller
              name={`insurances_attributes.${index}.insurance_number`} control={control} defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <div className="col-lg-4 col-md-4">
                  <div className="row">
                    <label htmlFor="patient_attributes.ssn" className="form-label col-lg-4 col-12">Phone{insuranceName?.toLowerCase() === "other" && (
    <span className="text-black">  *</span>
  )}</label>
                    <div className="col-lg-8 col-12">
                      <ReactInputMask
                        {...field}
                        mask="(999) 999-9999"
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      >
                        {(inputProps: {}) => <input type="text" id={`insurances_attributes.${index}.insurance_number`} className="form-control" placeholder="(555) 555-5555" {...inputProps} />}
                      </ReactInputMask>

                      {error && error.message ? (
                        <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2"></div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      {/* Pharmacy Insurance */}
      <div className="card">
        <div className="card-header bg-success-subtle">
          <h5 style={{ fontSize: '1.375rem', color: '#000' }}>Pharmacy Insurance</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-lg-6 col-md-6">
              <Controller
                name={`insurances_attributes.${index}.member_id`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">Rx Member ID</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRowInsideAnother={true}
                        isRequired={false}
                        // inputLabel="Rx Member ID"
                        inputId={`insurances_attributes.${index}.member_id`}
                        field={field}
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="col-lg-6 col-md-6">
              <Controller
                name={`insurances_attributes.${index}.bin`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">BIN</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRequired={false}
                        isRowInsideAnother={true}
                        field={field}
                        inputId={`insurances_attributes.${index}.bin`}
                        // inputLabel="BIN"
                        inputPlaceholder=""
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6 col-md-6">
              <Controller
                name={`insurances_attributes.${index}.pcn`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">PCN</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRowInsideAnother={true}
                        isRequired={false}
                        inputId={`insurances_attributes.${index}.pcn`}
                        // inputLabel="PCN"
                        field={field}
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="col-lg-6 col-md-6">
              <Controller
                name={`insurances_attributes.${index}.rx_group`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className={"row"}>
                    <label className="form-label col-lg-4 col-12">Rx Group</label>
                    <div className={"col-lg-8 col-12"}>
                      <HubInputField
                        isRowBased={true}
                        isRequired={false}
                        isRowInsideAnother={true}
                        field={field}
                        inputId={`insurances_attributes.${index}.rx_group`}
                        // inputLabel="Rx Group"
                        inputPlaceholder=""
                        error={error}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* <Controller
        name={`insurances_attributes.${index}.global_insurance_id`}
        control={control}
        render={() => (<></>)}
      />
      <Controller
        name={`insurances_attributes.${index}.is_primary_insurance`}
        control={control}
        render={() => (<></>)}
      />
      <Controller
        name={`insurances_attributes.${index}.insurance_priority`}
        control={control}
        defaultValue={index}
        render={() => (<></>)}
      /> */}

      {index !== 0 && (
        <div className="text-end mt-3">
          <button type="button" className="btn btn-danger btn-sm" onClick={() => removeInsurance(index)}>
            Delete this Insurance
          </button>
        </div>
      )}
      <Modal
        show={showOtherModal}
        onHide={() => {
          setShowOtherModal(false);
          setPendingOtherIndex(null);
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Please confirm you searched the Insurance list and could not
          find the insurance. Select Confirm to use Other.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              if (pendingOtherIndex !== null) {
                // setValue(
                //   `insurances_attributes.${pendingOtherIndex}.insurance_name`,
                //   ""
                // );
                 setValue(
                  `insurances_attributes.${pendingOtherIndex}.is_other_insurance`,
                  "no"
                );
              }
              setShowOtherModal(false);
              setPendingOtherIndex(null);
            }}
          >
            No
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              if (pendingOtherIndex !== null) {
                setValue(
                  `insurances_attributes.${pendingOtherIndex}.is_other_insurance`,
                  "yes"
                );
                // setValue(
                //   `insurances_attributes.${pendingOtherIndex}.plan_name`,
                //   "Other",
                //   {
                //     shouldValidate: true,
                //     shouldDirty: true,
                //     shouldTouch: true,
                //   }
                // );
              }

              setShowOtherModal(false);
              setPendingOtherIndex(null);
            }}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>

  );
};

export default SinglePatientInsurance;
